const _ = require("lodash");
const ParserHelpers = require("../util/ParserHelpers");
const FontUtil = require("../../util/Font");
const ColorUtil = require("../../util/ColorUtil");
const colors = ColorUtil.getDiagramColors();

/* CONSTANTS */

// Days per month in normal years (ie. not leap year)
const MONTHS = {
	January: 31,
	February: 28,
	March: 31,
	April: 30,
	May: 31,
	June: 30,
	July: 31,
	August: 31,
	September: 30,
	October: 31,
	November: 30,
	December: 31,
};

const DAYS_IN_WEEK = [
	"Sunday",
	"Monday",
	"Tuesday",
	"Wednesday",
	"Thursday",
	"Friday",
	"Saturday",
];

// Default styles from specs
const DEFAULT_MONTH_BG_COLOR = colors.mediumlightpinkred; //"#ff4d55";
const DEFAULT_DAY_BG_COLOR = colors.clightred; //"#ff8080";
const DEFAULT_TOP_LINE_COLOR = colors.cpinkred; // "#b93844";
const DEFAULT_TOP_TEXT_COLOR = colors.white; //"#ffffff"
const DEFAULT_MAIN_LINE_COLOR = colors.philippinesilver; //"#b3b3b3";
const DEFAULT_CALENDAR_DAYS_BG_COLOR = colors.white;
const NUMBERS_TEXT_COLOR = colors.darkcharcoal; //"#333333";
const UNUSED_DAYS_BG_COLOR = colors.cverylightgray; //"#d9d9d9";
const DEFAULT_MONTH_FONT = "bold 24px Roboto";
const DEFAULT_DAY_NAME_FONT = "14px Roboto";
const DEFAULT_NUMBERS_FONT = "bold 21px Roboto";

// Default settings
const DEFAULT_START_POSITION = 1; // first possible position (Sunday)
const DEFAULT_NUM_ROWS = 5;
const MIN_NUM_ROWS = 1; // could be changed to 0 if that's ever needed
const MAX_NUM_ROWS = 6;
const MAX_SCALE = 2.2;
const MIN_SCALE = 0.2;
const DATES_SEPARATOR = ";";

const VALID_DAY_NAME_OPTION_VALUES = [
	"letter",
	"l",
	"abbreviation",
	"a",
	"word",
	"w",
];

const DEFAULT_OPTIONS = {
	dayNames: "a", // default to abbreviation
	grayUnused: false,
	scale: 1.1,
	numRows: "", // set later (defaults to minimum needed to display all dates in month)
	skipMonth: false,
	color: DEFAULT_MONTH_BG_COLOR,
	blanks: [], // any active blanks
	emptyDates: [], // any empty dates in calendar
	leapYear: false,
};

// amount to lighten day names area fill color relative to the main color
const LIGHTEN_COLOR_AMOUNT = 20;
// amount to darken top area stroke color relative to the main color
const DARKEN_COLOR_AMOUNT = -20;
// value of unused day cells
const UNUSED_DAY_CELL_CONTENT = "";

//////////////////////////////
/* Parsing Helper Functions */
//////////////////////////////

// Process boolean optional arguments
// Returns object with boolean flag (whether parsing was successful) and argument value
const checkForBooleanArgs = function(ps, key, val) {
	let success = true; // flag for whether parsing succeeded
	if (_.includes(["grayUnused", "leapYear", "skipMonth"], key)) {
		if (typeof val === "undefined") {
			// if used without setting value (e.g. [leapYear])
			val = true;
		} else if (_.includes(["true", "false"], val)) {
			// if value is present, must be true or false
			val = val === "true"; // change valid string value into boolean
		} else {
			// invalid
			ps.pushError(
				`Optional argument ${key} is expected to be boolean. To use this option, type "[${key}]", "[${key}=true]" or "[${key}=false]".`
			);
			success = false;
		}
	} else if (!val) {
		// handle any non-boolean arguments missing a value
		ps.pushError(
			`The optional argument ${key} does not take a boolean value, so it must take the form [${key}=value].`
		);
		success = false;
	}
	return {success, val};
};

// Converts string input into numeric value (integer or float)
// Returns object with boolean flag and the numeric value
const processNumericVal = function(ps, success, key, val, isInteger, min, max) {
	val = isInteger ? parseInt(val) : parseFloat(val);
	const typeStr = isInteger ? "an integer" : "a numeric";
	if (_.isNaN(val) || val < min || val > max) {
		ps.pushError(
			`"${key}" requires ${typeStr} value between ${min} and ${max} (inclusive).`
		);
		success = false;
	}
	return {success, val};
};

// Returns string listing each item in array in quotes, separated by sep value
const getArrItemsAsString = (arr, sep = ",", end = "") =>
	arr.map(x => `"${x}"${end}`).join(sep);

// Returns boolean for whether an inputted key is a valid option
const validateKey = function(ps, options, key) {
	const keyInvalid = !_.keys(options).includes(key);
	if (keyInvalid) {
		const validOptionsStr = getArrItemsAsString(_.keys(options));
		ps.pushError(
			`"${key}" is not a valid choice. Key for optional argument must be one of: ${validOptionsStr}.`
		);
		return false;
	}
	return true;
};

// Looks up hex value of color name if valid
// Returns object with boolean flag and hex value
const processColor = function(ps, success, val) {
	if (val in colors) {
		val = colors[val];
	} else {
		const colorsStr = getArrItemsAsString(_.keys(colors), "\n", "");
		ps.pushError(
			`Specified value "${val}" is not a supported color. The "color" argument can accept any of the diagram colors: ${colorsStr}.`
		);
		success = false;
	}
	return {success, val};
};

// Removes any invalid values from the blanks/emptyDates array
const skipInvalids = function(ps, arr, invalidsArr, alertMessage) {
	if (!_.isEmpty(invalidsArr)) {
		const invalidsStr = getArrItemsAsString(invalidsArr);
		ps.pushError(
			`${alertMessage} The invalid value${
				invalidsArr.length > 1 ? "s" : ""
			} (${invalidsStr}) will be skipped.`
		);
		arr = arr.filter(b => !invalidsArr.includes(b));
	}
	return arr;
};

// Returns true if string value is a positive integer
const strIsPositiveInteger = str => /^([1-9][0-9]*)$/.test(str);

// Convert string value of blanks/emptyDates into an array of integers
const transformDates = function(ps, val) {
	if (val) {
		// split & filter out empty string
		let arr = val.split(DATES_SEPARATOR).filter(b => !!b);
		// skip any invalid blanks
		const invalidsArr = arr.filter(b => !strIsPositiveInteger(b));
		const alertMessage =
			"Each date must be entered as a positive integer, and be separated by semi-colons.";
		arr = skipInvalids(ps, arr, invalidsArr, alertMessage);
		// convert to integers, sort, and remove duplicates
		val = _.sortedUniq(_.sortBy(arr.map(b => parseInt(b))));
	}
	return val;
};

// Checks if the value provided is a member of the options array (optionsArr)
const validateStringVal = function(ps, key, val, optionsArr) {
	if (!optionsArr.includes(val)) {
		const validsStr = getArrItemsAsString(optionsArr);
		ps.pushError(
			`"${val}" is not a valid value. The ${key} argument accepts the following values: ${validsStr}`
		);
		return false;
	}
	return true;
};

// Processes and validates value
// Returns object with boolean flag and the processed value
const processVal = function(ps, key, val) {
	let success;
	({success, val} = checkForBooleanArgs(ps, key, val));
	if (key === "numRows") {
		const numRowsValInfo = [true, MIN_NUM_ROWS, MAX_NUM_ROWS]; // (isInteger, min value, max value)
		({success, val} = processNumericVal(
			ps,
			success,
			key,
			val,
			...numRowsValInfo
		));
	} else if (key === "scale") {
		const scaleValInfo = [false, MIN_SCALE, MAX_SCALE];
		({success, val} = processNumericVal(
			ps,
			success,
			key,
			val,
			...scaleValInfo
		));
	} else if (key === "color") {
		({success, val} = processColor(ps, success, val));
	} else if (key === "blanks") {
		val = transformDates(ps, val);
	} else if (key === "emptyDates") {
		val = transformDates(ps, val);
	} else if (key === "dayNames") {
		success = validateStringVal(ps, key, val, VALID_DAY_NAME_OPTION_VALUES);
		if (success) {
			val = val[0]; // store first letter of day name value
		}
	}
	return {success, val};
};

// Gets any valid optional arguments
// Returns object with boolean flag and the updated options object
const getOptionalArgs = function(ps) {
	const options = {...DEFAULT_OPTIONS};
	while (true) {
		const optionalArg = ParserHelpers.findOptionalArgument(ps);
		if (!optionalArg) {
			break;
		}
		const argStr = optionalArg ? optionalArg.getRest() : "";
		const [key, val] = argStr.split("=");

		// if key is invalid
		if (!validateKey(ps, options, key)) {
			return {success: false};
		}

		const {success: valIsValid, val: processedVal} = processVal(ps, key, val);

		// if value is invalid
		if (!valIsValid) {
			return {success: false};
		}

		options[key] = processedVal;
	}
	return {success: true, options};
};

// Returns true if inputPos is a valid start position
const validateStartPosition = function(ps, inputPos) {
	const pos = parseInt(inputPos);
	if (!Number.isInteger(pos)) {
		ps.pushError(
			`"${inputPos}" is invalid because the startPosition must be an integer.`
		);
		return false;
	}
	if (pos < 1 || pos > DAYS_IN_WEEK.length) {
		ps.pushError(
			`"${inputPos}" is invalid. The value of startPosition must be an integer between 1 and 7 (1 for Sunday, 4 for Wednesday, etc.).`
		);
		return false;
	}
	return true;
};

// Returns month if input is valid, otherwise returns false
const validateMonth = function(ps, inputMonth) {
	const monthNames = _.keys(MONTHS);

	// check if month was inputted as a valid number (1-12)
	const n = parseInt(inputMonth);
	if (!_.isNaN(n) && Number.isInteger(n) && n > 0 && n <= monthNames.length) {
		return monthNames[n - 1]; // adjust number to use it as index
	}

	// if not a valid number, check if month name was inputted as a string
	const month = _.capitalize(inputMonth); // case-insensitive for month names
	const monthsStr = getArrItemsAsString(monthNames);
	const numbersStr = getArrItemsAsString(_.range(1, monthNames.length + 1));
	if (!monthNames.includes(month)) {
		ps.pushError(
			`"${inputMonth}" is not a valid month. Must be one of ${monthsStr} or corresponding numbers ${numbersStr}. If you would also like to specify a start position, the correct syntax is {month;startPosition}.`
		);
		return false;
	} else {
		return month;
	}
};

// Processes the required month argument as well as start position (if specified)
// Returns object with boolean flag, month and startPosition values
const getRequiredArg = function(ps) {
	const reqArgs = ParserHelpers.findArguments(ps, 1, {requireBrace: true});
	const inputText = reqArgs ? reqArgs[0].text : "";
	let inputMonth, inputStartPosition;
	let success = true;
	let startPosition = DEFAULT_START_POSITION;

	// if start position is specified
	if (inputText.includes(";")) {
		[inputMonth, inputStartPosition] = inputText.split(";");
		success = validateStartPosition(ps, inputStartPosition);
		startPosition = parseInt(inputStartPosition);
	} else {
		inputMonth = inputText;
	}

	const month = validateMonth(ps, inputMonth);
	success = !month ? false : success;
	return {success, month, startPosition};
};

// remove any remaining invalid dates (greater than number of days in month)
const checkValidDates = function(ps, options, key, month, numDays) {
	const arr = options[key];
	const invalidNumbers = arr.filter(b => b > numDays);
	const alertMessage = `Every number for ${key} argument must be a possible date in ${month} (a number between 1 and ${numDays}).`;
	return skipInvalids(ps, arr, invalidNumbers, alertMessage);
};

const parser = function(ps) {
	// get all valid optional arguments provided
	const {success: optionalArgParsingSuccessful, options} = getOptionalArgs(ps);
	if (!optionalArgParsingSuccessful) return false;

	// get values for month and starting position (if possible)
	const {
		success: reqArgParsingSuccessful,
		month,
		startPosition,
	} = getRequiredArg(ps);
	if (!reqArgParsingSuccessful) return false;

	// get normal number of days per month
	let numDays = MONTHS[month];

	// handle leap year
	if (options.leapYear && month === "February") {
		numDays++;
	}

	// unless numRows was specified, adjust default number of rows based on month and starting position
	if (options.numRows === "") {
		options.numRows = DEFAULT_NUM_ROWS;
		const totalSlots = startPosition + numDays - 1;
		if (totalSlots > options.numRows * DAYS_IN_WEEK.length) {
			options.numRows++;
		}
	}

	// final validation for empty dates
	options.emptyDates = checkValidDates(
		ps,
		options,
		"emptyDates",
		month,
		numDays
	);

	// final processing for blanks
	let children = [];
	if (!_.isEmpty(options.blanks)) {
		// remove any remaining invalid blanks (greater than last day in month)
		options.blanks = checkValidDates(ps, options, "blanks", month, numDays);
		// make children nodes for blanks
		children = options.blanks.map(blank => ({
			type: "blank",
			blankType: "simple",
			answerSize: blank.toString().length,
			children: [],
		}));
	}

	return {
		type: "calendar",
		month,
		startPosition,
		numDays,
		children,
		...options,
	};
};

//////////////////////////////
/* Drawing Helper Functions */
//////////////////////////////

/* Color adjustment function */

// This function changes a given hexadecimal color by a certain amount.
// Comes from Martin Tournoij's comment on supersan's answer in this StackOverflow thread: https://stackoverflow.com/q/5560248/
const adjustColor = (hexColor, amountToAdjust) =>
	hexColor.replace(/\w\w/g, m =>
		Math.min(255, Math.max(0, parseInt(m, 16) + amountToAdjust)).toString(16)
	);

/* Basic shape drawing functions */

const fillAndOutlineRect = function(ctx, rect, props = {}) {
	const {fillColor, lineColor, lineWidth} = props;
	ctx.save();
	ctx.lineWidth = lineWidth;
	ctx
		.bp()
		.rect(rect)
		.fill(fillColor)
		.stroke(lineColor);
	ctx.restore();
};

const fillRect = function(ctx, rect, fillColor) {
	ctx.save();
	ctx
		.bp()
		.rect(rect)
		.fill(fillColor);
	ctx.restore();
};

const drawCenteredText = function(ctx, text, rect, props) {
	const {textColor, defaultFont, fontScaleFactor} = props;
	const [x, y, w, h] = rect;
	ctx.save();
	if (defaultFont) {
		ctx.font = defaultFont;
	}
	if (fontScaleFactor) {
		ctx.font = FontUtil.scaleSize(ctx.font, fontScaleFactor);
	}
	ctx.fillStyle = textColor;
	ctx.textAlign = "center";
	ctx.textBaseline = "middle";
	const centerX = x + w / 2.0;
	const centerY = y + h / 2.0;
	ctx.fillText(text, centerX, centerY);
	ctx.restore();
};

const drawLine = function(ctx, startCoords, endCoords, lineColor, lineWidth) {
	ctx.save();
	ctx.lineWidth = lineWidth;
	ctx.strokeStyle = lineColor;
	ctx.beginPath();
	ctx.moveTo(...startCoords);
	ctx.lineTo(...endCoords);
	ctx.stroke();
	ctx.closePath();
	ctx.restore();
};

/* Calendar area drawing functions */

const drawCellContent = function(
	ctx,
	cellContent,
	cellRect,
	props,
	blanks,
	emptyDates,
	rc
) {
	const {
		lineWidth,
		grayUnusedDaysOption,
		unusedDaysColor,
		usedDaysColor,
	} = props;
	const [x, y, w, h] = cellRect;

	// if option selected, grays out unused day cell
	const bgColor =
		grayUnusedDaysOption && cellContent === UNUSED_DAY_CELL_CONTENT
			? unusedDaysColor
			: usedDaysColor;

	// if needed, fill in background color of cell (only used in main numbers area)
	if (bgColor) {
		// reduce fill area by line width to avoid overlap with line stroke
		const fillColorRect = [
			x + lineWidth / 2.0,
			y + lineWidth / 2.0,
			w - lineWidth,
			h - lineWidth,
		];
		fillRect(ctx, fillColorRect, bgColor);
	}

	if (blanks.includes(cellContent)) {
		// add blank
		rc.pushBlank({
			type: "simple",
			x: x,
			y: y,
			width: w - lineWidth,
			height: h - lineWidth,
			answerSize: cellContent.toString().length,
		});
	} else if (!emptyDates.includes(cellContent)) {
		// draw cell text content
		drawCenteredText(ctx, cellContent, cellRect, props);
	}
};

const drawCellsInRow = function(
	ctx,
	rect,
	cellContentArr,
	cellWidth,
	props = {},
	blanks = [],
	emptyDates = [],
	rc = {}
) {
	const {lineWidth, lineColor} = props;
	const [x, y, , h] = rect;

	cellContentArr.forEach((cell, i) => {
		const cellX = x + cellWidth * i;
		const cellRect = [cellX, y, cellWidth, h];

		drawCellContent(ctx, cell, cellRect, props, blanks, emptyDates, rc);

		// draw dividing stroke between cell and its neighbor to the right
		if (i < cellContentArr.length - 1) {
			const rightX = cellX + cellWidth;
			drawLine(ctx, [rightX, y], [rightX, y + h], lineColor, lineWidth);
		}
	});
};

const drawMonthArea = function(ctx, rect, month, props = {}) {
	fillAndOutlineRect(ctx, rect, props);
	// draw name of month
	drawCenteredText(ctx, month, rect, props);
};

const drawDayNameArea = function(ctx, rect, daysOption, cellWidth, props = {}) {
	// draw bounding box
	fillAndOutlineRect(ctx, rect, props);

	// get the cell contents
	let daysArr = DAYS_IN_WEEK;
	if (daysOption === "w") {
		// display whole word
		daysArr = DAYS_IN_WEEK;
		props.fontScaleFactor *= 0.5; // make text smaller to fit
	} else if (daysOption === "a") {
		// display abbreviation
		daysArr = DAYS_IN_WEEK.map(day => day.slice(0, 3)); // first 3 letters
	} else {
		// display letters
		daysArr = DAYS_IN_WEEK.map(day => day[0]);
	}

	drawCellsInRow(ctx, rect, daysArr, cellWidth, props);
};

const getRowCellContents = function(
	rowIndex,
	startNum,
	startPosition,
	numDays
) {
	const WEEK_LEN = DAYS_IN_WEEK.length; // number of days in week
	let numbersArr;

	// Returns array of length n, where each item is the value of UNUSED_DAY_CELL_CONTENT
	const getUnusedArr = n => new Array(n).fill(UNUSED_DAY_CELL_CONTENT);

	// first row (handle special case of any unused positions before the start position)
	if (rowIndex === 0 && startPosition > 1) {
		const numPriorUnusedDays = startPosition - 1;
		const numUsedDays = WEEK_LEN - startPosition + 1;
		const lastDayShownInRow = startNum + numUsedDays - 1;
		numbersArr = getUnusedArr(numPriorUnusedDays).concat(
			_.range(startNum, lastDayShownInRow + 1)
		);
		startNum = lastDayShownInRow + 1;
	} else {
		// normal case
		if (startNum > numDays) {
			// all days in this row are unused
			numbersArr = getUnusedArr(WEEK_LEN);
		} else if (startNum + WEEK_LEN > numDays) {
			// some unused days
			numbersArr = _.range(startNum, numDays + 1);
			const numUnusedDays = WEEK_LEN - (numDays - startNum) - 1;
			numbersArr = numbersArr.concat(getUnusedArr(numUnusedDays));
		} else {
			// no unused days
			numbersArr = _.range(startNum, startNum + WEEK_LEN);
		}
		startNum += WEEK_LEN;
	}
	return [startNum, numbersArr];
};

const drawNumbersArea = function(
	rc,
	ctx,
	rect,
	cellWidth,
	cellHeight,
	numRows,
	numDays,
	startPosition,
	blanks,
	emptyDates,
	props
) {
	const [x, y, w, h] = rect;
	const {lineColor, lineWidth} = props;
	let rowY = y;
	let startNum = 1;
	for (let i = 0; i < numRows; i++) {
		let numbersArr;

		// get contents of row
		[startNum, numbersArr] = getRowCellContents(
			i,
			startNum,
			startPosition,
			numDays
		);

		// draw contents of row
		const rowRect = [x, rowY, w, cellHeight];
		drawCellsInRow(
			ctx,
			rowRect,
			numbersArr,
			cellWidth,
			props,
			blanks,
			emptyDates,
			rc
		);

		// draw horizontal line beneath row
		const bottomY = rowY + cellHeight;
		let [leftX, rightX] = [x, x + w];
		if (i === numRows - 1) {
			// if last row, increase line length to fill in corner gaps
			leftX -= lineWidth / 2.0;
			rightX += lineWidth / 2.0;
		}
		drawLine(ctx, [leftX, bottomY], [rightX, bottomY], lineColor, lineWidth);

		rowY += cellHeight;
	}

	// draw vertical left and right lines around main numbers area in calendar
	const bottomY = y + h;
	drawLine(ctx, [x, y], [x, bottomY], lineColor, lineWidth);
	drawLine(ctx, [x + w, y], [x + w, bottomY], lineColor, lineWidth);
};

const nodeType = {
	draw(rc, x, y, node, measure) {
		const {
			top,
			calendarWidth,
			cellHeight,
			cellWidth,
			numbersAreaHeight,
			dayNameAreaHeight,
			monthAreaHeight,
			lineWidth,
			scaleFactor,
		} = measure;
		const {
			color,
			month,
			skipMonth,
			dayNames,
			numRows,
			numDays,
			grayUnused,
			startPosition,
			blanks,
			emptyDates,
		} = node;
		const ctx = rc.context;
		let currY = y - top;

		// if not default color, get color of the top area lines by darkening the main color
		const topLineColor =
			color === DEFAULT_MONTH_BG_COLOR
				? DEFAULT_TOP_LINE_COLOR
				: adjustColor(color, DARKEN_COLOR_AMOUNT);

		// canvas stroke extends halfway into pixels on either side of path,
		// so x and y can be adjusted to account for this (may not be necessary)
		x = Math.floor(x) + lineWidth / 2.0;
		y = Math.floor(y) + lineWidth / 2.0;

		// Draw month area (if skip option not selected)
		if (!skipMonth) {
			const monthAreaRect = [x, currY, calendarWidth, monthAreaHeight];
			const monthProps = {
				fillColor: color,
				lineColor: topLineColor,
				lineWidth,
				textColor: DEFAULT_TOP_TEXT_COLOR,
				fontScaleFactor: scaleFactor,
				defaultFont: DEFAULT_MONTH_FONT,
			};
			drawMonthArea(ctx, monthAreaRect, month, monthProps);
			currY += monthAreaHeight;
		}

		// Draw day name area
		const dayNameAreaRect = [x, currY, calendarWidth, dayNameAreaHeight];

		// if not default color, get fill color of the day name area by lightening the main color
		const dayNameAreaFillColor =
			color === DEFAULT_MONTH_BG_COLOR
				? DEFAULT_DAY_BG_COLOR
				: adjustColor(color, LIGHTEN_COLOR_AMOUNT);

		const dayProps = {
			fillColor: dayNameAreaFillColor,
			lineColor: topLineColor,
			textColor: DEFAULT_TOP_TEXT_COLOR,
			defaultFont: DEFAULT_DAY_NAME_FONT,
			lineWidth,
			fontScaleFactor: scaleFactor,
		};
		drawDayNameArea(ctx, dayNameAreaRect, dayNames, cellWidth, dayProps);

		// Draw main calendar numbers area
		currY += dayNameAreaHeight + lineWidth / 2.0;
		const numbersAreaRect = [x, currY, calendarWidth, numbersAreaHeight];
		const numbersProps = {
			usedDaysColor: DEFAULT_CALENDAR_DAYS_BG_COLOR,
			lineColor: DEFAULT_MAIN_LINE_COLOR,
			textColor: NUMBERS_TEXT_COLOR,
			defaultFont: DEFAULT_NUMBERS_FONT,
			lineWidth,
			fontScaleFactor: scaleFactor,
			unusedDaysColor: UNUSED_DAYS_BG_COLOR,
			grayUnusedDaysOption: grayUnused,
		};
		drawNumbersArea(
			rc,
			ctx,
			numbersAreaRect,
			cellWidth,
			cellHeight,
			numRows,
			numDays,
			startPosition,
			blanks,
			emptyDates,
			numbersProps
		);
	},
	measure(rc, fontInfo, props, node) {
		const numWeekDays = DAYS_IN_WEEK.length;
		const fontSize = fontInfo.size;

		// base measurements from the specs
		const BASE_FONT_SIZE = 21;
		const BASE_FONT_HEIGHT = 15.2783;
		const BASE_CELL_HEIGHT = 36.5243;
		const BASE_CELL_WIDTH = 42.8571;
		const BASE_DAY_AREA_HEIGHT = 19;
		const BASE_MONTH_AREA_HEIGHT = 37.5;
		const BASE_LINE_WIDTH = 1;

		// adjust base measurements based on actual font size
		const scaleFactor = node.scale * (fontSize / BASE_FONT_SIZE);
		const fontHeight = BASE_FONT_HEIGHT * scaleFactor;
		const cellHeight = (fontHeight * BASE_CELL_HEIGHT) / BASE_FONT_HEIGHT;
		const cellWidth = (cellHeight * BASE_CELL_WIDTH) / BASE_CELL_HEIGHT;

		// get line width at scale (as whole number >= 1.0px)
		const lineWidth = Math.max(1.0, Math.round(BASE_LINE_WIDTH * scaleFactor));

		// calculate totals
		const numbersAreaHeight = cellHeight * node.numRows;
		const dayNameAreaHeight =
			(cellHeight * BASE_DAY_AREA_HEIGHT) / BASE_CELL_HEIGHT;
		const monthAreaHeight = node.skipMonth
			? 0
			: (cellHeight * BASE_MONTH_AREA_HEIGHT) / BASE_CELL_HEIGHT;

		const totalWidth = cellWidth * numWeekDays + lineWidth;
		const totalHeight =
			numbersAreaHeight + dayNameAreaHeight + monthAreaHeight + lineWidth;

		// measure children for the cache entry
		node.children.forEach(child => {
			rc.measureNode(rc, fontInfo, props, child);
		});

		return {
			width: totalWidth,
			calendarWidth: totalWidth - lineWidth,
			top: totalHeight / 2,
			bottom: totalHeight / 2,
			cellHeight,
			cellWidth,
			numbersAreaHeight,
			dayNameAreaHeight,
			monthAreaHeight,
			lineWidth,
			scaleFactor,
		};
	},
	readAloud(node, context) {
		return "";
	},
};

module.exports = {
	nodes: {
		calendar: nodeType,
	},
	parsers: {
		mathCommands: {
			calendar: parser,
		},
	},
};
